import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { CalendarBlank, Heart, ChatCircle, Lightning, ArrowRight, Clock, CurrencyDollar, MagnifyingGlass, FileText, CheckCircle, CaretLeft, CaretRight, Star, Sparkle, Warning, ShieldCheck, HandWaving, Confetti, PushPin, Check } from '@phosphor-icons/react';
import { StatCardSkeleton, TableSkeleton } from '@/components/Skeletons';
import { describePolicy, computeCancellation } from '@/lib/policies';
import { providerPrice } from '@/lib/pricing';
import { toast } from '@/components/ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Confirm-and-cancel a booking, showing the fee/refund the customer would face
function CancelModal({ booking, headers, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const est = computeCancellation(booking);
  const providerName = booking.provider?.business_name || 'this provider';
  const money = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const submit = async () => {
    setBusy(true); setError('');
    try {
      await axios.put(`${API}/bookings/${booking.booking_id}/status?status=cancelled`, {}, { headers });
      onDone();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not cancel this booking. Please try again.');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-foreground/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} role="dialog" aria-modal="true" data-testid="cancel-modal">
      <div className="bg-card rounded-2xl shadow-lift p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 mb-1">
          <span className="w-9 h-9 rounded-xl bg-red-500/10 text-red-500 flex items-center justify-center shrink-0"><Warning size={18} weight="fill" /></span>
          <div>
            <h3 className="font-display text-lg font-semibold text-foreground leading-tight">Cancel this booking?</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{providerName} · {booking.service?.title || booking.category} · {booking.booking_date}</p>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground leading-relaxed bg-secondary/50 rounded-lg px-3 py-2 my-4">{describePolicy(booking)}</p>
        {est.paid > 0 ? (
          <div className="rounded-xl border border-border/70 divide-y divide-border/60 text-sm mb-3" data-testid="cancel-refund-breakdown">
            <div className="flex justify-between px-3.5 py-2.5"><span className="text-muted-foreground">Paid so far</span><span className="text-foreground tabular-nums">{money(est.paid)}</span></div>
            {est.fee > 0 && <div className="flex justify-between px-3.5 py-2.5"><span className="text-muted-foreground">Cancellation fee</span><span className="text-red-500 tabular-nums">−{money(est.fee)}</span></div>}
            <div className="flex justify-between px-3.5 py-2.5 bg-secondary/40"><span className="font-semibold text-foreground">Estimated refund</span><span className="font-semibold text-foreground tabular-nums">{money(est.refund)}</span></div>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground mb-3">You haven't paid for this booking yet, so cancelling won't cost anything.</p>
        )}
        {est.paid > 0 && <p className="text-[11px] text-muted-foreground/80 mb-4">Refunds process to your original payment method in 5–10 business days. The final amount is confirmed against the provider's policy at processing time.</p>}
        {error && <p className="text-xs text-red-500 mb-3" data-testid="cancel-error">{error}</p>}
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 h-11 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:bg-secondary/60 btn-anim cursor-pointer">Keep booking</button>
          <button onClick={submit} disabled={busy}
            className="flex-1 h-11 rounded-xl bg-red-500 text-white text-sm font-semibold btn-anim shadow-soft hover:bg-red-600 disabled:opacity-50 cursor-pointer"
            data-testid="cancel-confirm">
            {busy ? 'Cancelling…' : 'Cancel booking'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal for rating + reviewing a completed booking
function ReviewModal({ booking, headers, onClose, onDone }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!rating) { setError('Pick a star rating first.'); return; }
    setBusy(true);
    setError('');
    try {
      await axios.post(`${API}/reviews`, { booking_id: booking.booking_id, rating, comment: comment.trim() }, { headers });
      onDone();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not save your review. Please try again.');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-foreground/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} role="dialog" aria-modal="true" data-testid="review-modal">
      <div className="bg-card rounded-2xl shadow-lift p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-lg font-semibold text-foreground mb-1">How was {booking.provider}?</h3>
        <p className="text-xs text-muted-foreground mb-5">{booking.service} · {booking.date}</p>
        <div className="flex gap-1.5 mb-5" onMouseLeave={() => setHover(0)}>
          {[1, 2, 3, 4, 5].map(n => (
            <button key={n} type="button" onClick={() => setRating(n)} onMouseEnter={() => setHover(n)}
              aria-label={`${n} star${n > 1 ? 's' : ''}`}
              className="cursor-pointer btn-anim" data-testid={`star-${n}`}>
              <Star size={30} weight={(hover || rating) >= n ? 'fill' : 'regular'}
                className={(hover || rating) >= n ? 'text-amber-400' : 'text-muted-foreground/40'} />
            </button>
          ))}
        </div>
        <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
          placeholder="Tell others what it was like (optional)…"
          className="w-full rounded-xl border border-border bg-card px-3 py-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none mb-3"
          data-testid="review-comment" />
        {error && <p className="text-xs text-red-500 mb-3" data-testid="review-error">{error}</p>}
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 h-11 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:bg-secondary/60 btn-anim cursor-pointer">Not now</button>
          <button onClick={submit} disabled={busy}
            className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground text-sm font-semibold btn-anim shadow-soft hover:bg-primary/90 disabled:opacity-50 cursor-pointer"
            data-testid="review-submit">
            {busy ? 'Saving…' : 'Submit review'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Report a problem with a booking — opens a dispute the Bookr team reviews (48h)
function ReportModal({ booking, headers, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const providerName = booking.provider?.business_name || 'this provider';

  const submit = async () => {
    if (reason.trim().length < 10) { setError('Please add a few words about what went wrong.'); return; }
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/disputes`, { booking_id: booking.booking_id, description: reason.trim() }, { headers });
      onDone();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not submit your report. Please try again.');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-foreground/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} role="dialog" aria-modal="true" data-testid="report-modal">
      <div className="bg-card rounded-2xl shadow-lift p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 mb-1">
          <span className="w-9 h-9 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center shrink-0"><Warning size={18} weight="fill" /></span>
          <div>
            <h3 className="font-display text-lg font-semibold text-foreground leading-tight">Report a problem</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{providerName} · {booking.service?.title || booking.category} · {booking.booking_date}</p>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground leading-relaxed bg-secondary/50 rounded-lg px-3 py-2 my-4">Covered by the <span className="font-medium text-foreground">Bookr Guarantee</span> — our team reviews every report within 48 hours and will help make it right.</p>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4}
          placeholder="What went wrong? The more detail you give, the faster we can help."
          className="w-full rounded-xl border border-border bg-card px-3 py-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none mb-3"
          data-testid="report-reason" />
        {error && <p className="text-xs text-red-500 mb-3" data-testid="report-error">{error}</p>}
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 h-11 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:bg-secondary/60 btn-anim cursor-pointer">Never mind</button>
          <button onClick={submit} disabled={busy}
            className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground text-sm font-semibold btn-anim shadow-soft hover:bg-primary/90 disabled:opacity-50 cursor-pointer"
            data-testid="report-submit">
            {busy ? 'Submitting…' : 'Submit report'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CustomerDashboard() {
  const { user, token } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [myReviews, setMyReviews] = useState([]);
  const [reviewFor, setReviewFor] = useState(null);
  const [cancelFor, setCancelFor] = useState(null);
  const [reportFor, setReportFor] = useState(null);
  const [disputes, setDisputes] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [quoteBusy, setQuoteBusy] = useState(null);
  const [payingId, setPayingId] = useState(null);
  const [plans, setPlans] = useState([]);
  const [tab, setTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    try {
      const [bookRes, favRes, revRes, quoteRes, dispRes, planRes] = await Promise.all([
        axios.get(`${API}/bookings`, { headers }),
        axios.get(`${API}/favorites`, { headers }),
        axios.get(`${API}/profile/reviews`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/quotes`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/disputes`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/ai-planner/plans`, { headers }).catch(() => ({ data: [] }))
      ]);
      setBookings(bookRes.data);
      setFavorites(favRes.data);
      setMyReviews(Array.isArray(revRes.data) ? revRes.data : []);
      setQuotes(Array.isArray(quoteRes.data) ? quoteRes.data : []);
      setDisputes(Array.isArray(dispRes.data) ? dispRes.data : []);
      setPlans(Array.isArray(planRes.data) ? planRes.data : []);
    } catch (err) { console.error("Dashboard data error:", err); } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const statusColor = (s) => ({
    pending: 'bg-amber-500/10 text-amber-600',
    confirmed: 'bg-secondary text-primary',
    completed: 'bg-emerald-500/10 text-emerald-600',
    cancelled: 'bg-red-500/10 text-red-600',
  }[s] || 'bg-secondary text-muted-foreground');

  const statusIcon = (s) => {
    if (s === 'confirmed' || s === 'completed') return <CheckCircle size={12} weight="fill" className="inline mr-0.5" />;
    return null;
  };

  const displayBookings = bookings.map(b => ({
    provider: b.provider?.business_name || 'Provider',
    service: b.service?.title || b.category,
    date: b.booking_date,
    total: `$${b.total_paid}`,
    status: b.status,
    booking_id: b.booking_id,
    provider_id: b.provider_id,
    // Deep-link "Book again" straight back to this provider (and the exact
    // service if we still have it) instead of dumping them on /browse.
    rebookUrl: b.provider?.profile_id
      ? (b.service_id ? `/checkout/${b.provider.profile_id}/${b.service_id}` : `/provider/${b.provider.profile_id}`)
      : '/browse',
    balanceDue: b.status === 'confirmed' && (b.balance_amount || 0) > 0 && !b.balance_paid ? b.balance_amount : 0,
    balanceDueDate: b.balance_due,
    _raw: b,
  }));

  const payBalance = async (bookingId) => {
    if (payingId) return;               // guard against double-click / duplicate charge
    setPayingId(bookingId);
    try {
      const r = await axios.post(`${API}/bookings/${bookingId}/pay-balance`, { origin_url: window.location.origin }, { headers });
      if (r.data.url) window.location.href = r.data.url;
      else setPayingId(null);
    } catch (err) {
      console.error('Pay balance failed:', err);
      toast.error(err?.response?.data?.detail || 'Could not start payment. Please try again.');
      setPayingId(null);
    }
  };

  const reviewedIds = useMemo(() => new Set(myReviews.map(r => r.booking_id)), [myReviews]);
  const reportedIds = useMemo(() => new Set(disputes.map(d => d.booking_id)), [disputes]);

  const totalSpent = bookings
    .filter(b => b.status !== 'cancelled')
    .reduce((sum, b) => sum + (Number(b.total_paid) || 0), 0);
  const todayStr = new Date().toISOString().slice(0, 10);
  const upcomingBookings = bookings.filter(b =>
    ['pending', 'confirmed'].includes(b.status) && (b.booking_date || '') >= todayStr
  );
  const upcomingCount = upcomingBookings.length;
  // Saved AI plans the customer started — a strong "come back and finish" hook.
  // Derive a friendly title (their name, else the first thing they typed).
  const savedPlans = useMemo(() => plans.map(p => {
    const firstUserMsg = (p.messages || []).find(m => m.role === 'user');
    const title = (p.name && p.name.trim())
      || (firstUserMsg?.content || '').slice(0, 48).trim()
      || 'Untitled plan';
    return { plan_id: p.plan_id, title, msgCount: (p.messages || []).length, pinned: !!p.pinned };
  }), [plans]);

  // A brand-new customer: no bookings AND nothing else going on. They get a warm
  // guided welcome instead of a wall of zero-value stats.
  const isNewCustomer = !loading && bookings.length === 0 && quotes.length === 0 && favorites.length === 0 && plans.length === 0;

  // The soonest upcoming event = all upcoming bookings that share the nearest date.
  // This powers a "your event is in X days" countdown — the main reason to come
  // back and check on the plan.
  const nextEvent = useMemo(() => {
    if (!upcomingBookings.length) return null;
    // Only consider clean ISO YYYY-MM-DD dates — a malformed/demo date (e.g.
    // "Mar 15, 2026") must never reach the day math or it renders "NaN days".
    const isIso = (s) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}/.test(s);
    const soonestDate = upcomingBookings
      .map(b => b.booking_date)
      .filter(isIso)
      .sort()[0];
    if (!soonestDate) return null;
    const items = upcomingBookings.filter(b => b.booking_date === soonestDate);
    // Whole-day difference to the event date. Use UTC on BOTH sides so it
    // matches the UTC `todayStr` filter above and can't drift by a day at a
    // timezone boundary.
    const [y, m, d] = soonestDate.slice(0, 10).split('-').map(Number);
    if ([y, m, d].some(Number.isNaN)) return null;
    const evt = Date.UTC(y, m - 1, d);
    const now = new Date();
    const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    const days = Math.round((evt - today) / 86400000);
    return { date: soonestDate, items, days };
  }, [upcomingBookings]);

  const countdownLabel = (days) =>
    days <= 0 ? 'Today' : days === 1 ? 'Tomorrow' : `In ${days} days`;
  const prettyDate = (iso) => {
    try {
      const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
    } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="customer-dashboard">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Greeting — a brand-new customer isn't "back" yet */}
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight text-foreground">
            {isNewCustomer
              ? <>Welcome{user?.name ? `, ${user.name.split(' ')[0]}` : ''} <HandWaving size={20} weight="fill" className="inline text-primary" /></>
              : <>Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}</>}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isNewCustomer ? "Let's plan your event — here's how to get started." : 'Your bookings and saved providers — all in one place.'}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-border mb-8 overflow-x-auto">
          {[
            { id: 'dashboard', label: 'Dashboard' },
            { id: 'bookings', label: 'Bookings' },
            { id: 'quotes', label: `Quotes${quotes.filter(q => q.status === 'quoted').length ? ` (${quotes.filter(q => q.status === 'quoted').length})` : ''}` },
            { id: 'calendar', label: 'Calendar' },
            { id: 'favorites', label: 'Saved' },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors cursor-pointer ${tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              data-testid={`tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Dashboard tab */}
        {tab === 'dashboard' && loading && (
          <div className="space-y-8" data-testid="dashboard-loading">
            <StatCardSkeleton count={4} />
            <TableSkeleton rows={4} cols={5} />
          </div>
        )}
        {/* Brand-new customer: a warm, guided welcome instead of a wall of zeros */}
        {tab === 'dashboard' && isNewCustomer && (
          <div className="space-y-6" data-testid="customer-welcome">
            <div className="relative overflow-hidden bg-gradient-to-br from-primary/10 via-card to-secondary/40 border border-primary/20 rounded-3xl shadow-soft p-7 sm:p-9 fade-up">
              <div className="max-w-xl">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-primary bg-primary/10 rounded-full px-3 py-1 mb-4"><Sparkle size={12} weight="fill" /> Your event, handled</span>
                <h2 className="text-2xl sm:text-3xl font-display font-semibold tracking-tight text-foreground mb-2">Let's plan something great</h2>
                <p className="text-sm text-muted-foreground leading-relaxed mb-6">Tell our AI planner what you're celebrating and it'll build your whole event — trusted DJs, caterers, photographers and more — with prices that fit your budget. Every booking is protected by the Bookr Guarantee.</p>
                <div className="flex flex-col sm:flex-row gap-2.5">
                  <Link to="/ai-planner" className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 no-underline"><Lightning size={16} weight="fill" /> Plan with AI</Link>
                  <Link to="/browse" className="inline-flex items-center justify-center gap-2 border border-border bg-card text-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim hover:bg-secondary/60 hover:text-primary no-underline">Browse providers <ArrowRight size={15} /></Link>
                </div>
              </div>
            </div>

            {/* Three simple steps — set expectations, reduce first-time anxiety */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { n: '1', icon: <Lightning size={18} weight="fill" />, title: 'Tell us your event', body: 'Birthday, wedding, corporate — share the vibe, guest count and budget.' },
                { n: '2', icon: <Sparkle size={18} weight="fill" />, title: 'Get a full plan', body: 'We match you with trusted, available vendors and lay out the whole day.' },
                { n: '3', icon: <ShieldCheck size={18} weight="fill" />, title: 'Book with confidence', body: 'Pay securely in one place. Your money is protected until the event is done.' },
              ].map((s, i) => (
                <div key={s.n} className="bg-card border border-border/70 rounded-2xl shadow-soft p-5 fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">{s.icon}</span>
                    <span className="text-[11px] font-semibold text-muted-foreground">STEP {s.n}</span>
                  </div>
                  <p className="text-sm font-semibold text-foreground mb-1">{s.title}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>

            {/* Quick shortcuts still available */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link to="/ai-planner" className="bg-card rounded-2xl border border-border/70 shadow-soft card-lift p-4 flex items-center gap-3 fade-up no-underline"><Lightning size={20} className="text-accent-coral shrink-0" /><div><p className="text-sm font-medium text-foreground">AI Planner</p><p className="text-xs text-muted-foreground">Plan your event</p></div></Link>
              <Link to="/browse" className="bg-card rounded-2xl border border-border/70 shadow-soft card-lift p-4 flex items-center gap-3 fade-up no-underline"><MagnifyingGlassIcon /><div><p className="text-sm font-medium text-foreground">Browse</p><p className="text-xs text-muted-foreground">Find providers</p></div></Link>
              <Link to="/invitations" className="bg-card rounded-2xl border border-border/70 shadow-soft card-lift p-4 flex items-center gap-3 fade-up no-underline"><Sparkle size={20} className="text-primary shrink-0" /><div><p className="text-sm font-medium text-foreground">Invitations</p><p className="text-xs text-muted-foreground">Send digital invites</p></div></Link>
              <Link to="/messages" className="bg-card rounded-2xl border border-border/70 shadow-soft card-lift p-4 flex items-center gap-3 fade-up no-underline"><ChatCircle size={20} className="text-primary shrink-0" /><div><p className="text-sm font-medium text-foreground">Messages</p><p className="text-xs text-muted-foreground">Chat with providers</p></div></Link>
            </div>
          </div>
        )}

        {tab === 'dashboard' && !loading && !isNewCustomer && (
          <div className="space-y-8">
            {/* ═══ NEXT EVENT COUNTDOWN — the reason to come back. Shows how many
                days until your soonest event, the vendors booked for it, and a
                one-tap jump into the Event Coordinator to prep for the day. ═══ */}
            {nextEvent && (
              <div className="relative overflow-hidden bg-primary text-primary-foreground rounded-3xl shadow-lift p-6 sm:p-7 fade-up" data-testid="next-event-countdown">
                <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-primary-foreground/10 blur-2xl" aria-hidden="true" />
                <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
                  {/* Big countdown */}
                  <div className="shrink-0 text-center sm:text-left sm:border-r sm:border-primary-foreground/20 sm:pr-6">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-primary-foreground/70 mb-1">Your next event</p>
                    <p className="text-4xl sm:text-5xl font-display font-semibold leading-none">
                      {nextEvent.days <= 0 ? <Confetti size={44} weight="fill" className="inline" /> : nextEvent.days}
                      {nextEvent.days > 0 && <span className="text-lg font-normal text-primary-foreground/70 ml-1">{nextEvent.days === 1 ? 'day' : 'days'}</span>}
                    </p>
                    <p className="text-sm font-medium mt-1">{countdownLabel(nextEvent.days)}</p>
                  </div>
                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <p className="text-lg font-display font-semibold leading-tight">{prettyDate(nextEvent.date)}</p>
                    <p className="text-sm text-primary-foreground/80 mt-0.5">
                      {nextEvent.items.length} {nextEvent.items.length === 1 ? 'vendor' : 'vendors'} booked
                      {' · '}{nextEvent.items.map(b => b.provider?.business_name || b.category).filter(Boolean).slice(0, 3).join(', ')}
                      {nextEvent.items.length > 3 ? ` +${nextEvent.items.length - 3} more` : ''}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-4">
                      <Link to="/coordinator" className="inline-flex items-center gap-1.5 bg-primary-foreground text-primary rounded-xl px-4 h-10 text-sm font-semibold btn-anim shadow-soft hover:opacity-95 no-underline"><Sparkle size={15} weight="fill" /> Open Event Coordinator</Link>
                      <Link to="/messages" className="inline-flex items-center gap-1.5 bg-primary-foreground/15 text-primary-foreground rounded-xl px-4 h-10 text-sm font-semibold btn-anim hover:bg-primary-foreground/25 no-underline"><ChatCircle size={15} /> Message vendors</Link>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Event Coordinator entry — only when there's an upcoming booking but
                no dated next-event card (e.g. booking without a firm date). */}
            {upcomingCount > 0 && !nextEvent && (
              <Link to="/coordinator" className="flex items-center gap-4 bg-gradient-to-br from-primary/10 via-card to-secondary/40 border border-primary/20 rounded-2xl shadow-soft p-5 hover:shadow-lift transition-shadow fade-up group no-underline" data-testid="coordinator-banner">
                <span className="w-11 h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0 shadow-soft"><Sparkle size={20} weight="fill" /></span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground">Your Event Coordinator is ready</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">Vendors booked — now let AI help you confirm details, build a run-of-show, track payments, and nail the day.</p>
                </div>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary shrink-0 group-hover:gap-2.5 transition-all">Open <ArrowRight size={15} /></span>
              </Link>
            )}

            {/* Pick up where you left off — surfaces saved AI plans so a customer
                who started planning has a one-tap way back in. Strong come-back hook. */}
            {savedPlans.length > 0 && (
              <div className="fade-up" data-testid="saved-plans">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display text-lg font-semibold text-foreground">Pick up where you left off</h3>
                  <Link to="/ai-planner" className="text-xs font-medium text-primary hover:text-primary/80 transition-colors px-2 py-2 -my-2 no-underline">Open planner</Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {savedPlans.slice(0, 3).map((p, i) => (
                    <Link key={p.plan_id} to="/ai-planner" className="group bg-card border border-border/70 rounded-2xl shadow-soft card-lift p-4 flex items-start gap-3 no-underline fade-up" style={{ animationDelay: `${i * 50}ms` }} data-testid={`saved-plan-${p.plan_id}`}>
                      <span className="w-9 h-9 rounded-xl bg-accent-coral/10 text-accent-coral flex items-center justify-center shrink-0"><Lightning size={17} weight="fill" /></span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate">{p.title}{p.pinned && <PushPin size={12} weight="fill" className="inline ml-1 text-primary" />}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Draft plan{p.msgCount ? ` · ${Math.ceil(p.msgCount / 2)} messages` : ''}</p>
                      </div>
                      <span className="inline-flex items-center text-primary shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"><ArrowRight size={15} /></span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dashboard-stats">
              {[
                { label: 'Total bookings', value: bookings.length, sub: 'All time', icon: <CalendarBlank size={18} /> },
                { label: 'Total spent', value: `$${totalSpent.toLocaleString(undefined, { maximumFractionDigits: 2 })}`, sub: 'All time', icon: <CurrencyDollar size={18} /> },
                { label: 'Upcoming', value: upcomingCount, sub: 'Pending & confirmed', icon: <Clock size={18} /> },
                { label: 'Saved', value: favorites.length, sub: 'Saved providers', icon: <Heart size={18} /> },
              ].map((s, i) => (
                <div key={s.label} className="bg-card rounded-2xl border border-border/70 shadow-soft p-5 fade-up" style={{ animationDelay: `${Math.min(i, 8) * 50}ms` }} data-testid={`stat-${s.label.replace(/\s/g, '-').toLowerCase()}`}>
                  <div className="flex items-center gap-2 mb-2"><span className="text-primary">{s.icon}</span><span className="text-sm text-muted-foreground">{s.label}</span></div>
                  <div className="text-3xl font-display font-semibold text-foreground">{s.value}</div>
                  <div className="text-xs text-muted-foreground mt-1">{s.sub}</div>
                </div>
              ))}
            </div>

            {/* Booking history */}
            <div className="fade-up" style={{ animationDelay: '50ms' }} data-testid="booking-history">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-lg font-semibold text-foreground">Booking history</h3>
                <button onClick={() => setTab('bookings')} className="text-xs font-medium text-primary hover:text-primary/80 transition-colors cursor-pointer px-2 py-2 -my-2">View all</button>
              </div>
              {displayBookings.length === 0 ? (
                <div className="flex flex-col items-center text-center py-16 bg-card rounded-2xl border border-border/70 shadow-soft">
                  <div className="bg-secondary rounded-full p-4 mb-4">
                    <CalendarBlank size={28} className="text-primary" />
                  </div>
                  <p className="text-sm text-muted-foreground mb-5">No bookings yet — plan your first event and they'll show up here.</p>
                  <Link to="/ai-planner" className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90">
                    Plan an event <ArrowRight size={16} />
                  </Link>
                </div>
              ) : (
              <div className="bg-card rounded-2xl border border-border/70 shadow-soft overflow-hidden overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50 border-b border-border">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Provider</th>
                      <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold hidden md:table-cell">Service</th>
                      <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold hidden md:table-cell">Date</th>
                      <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Total</th>
                      <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Status</th>
                      <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayBookings.slice(0, 4).map((b, i) => (
                      <tr key={i} className="border-b border-border/60 last:border-0 hover:bg-secondary/50 transition-colors">
                        <td className="px-4 py-3 font-medium text-foreground">{b.provider}</td>
                        <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{b.service}</td>
                        <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{b.date}</td>
                        <td className="px-4 py-3 font-medium text-foreground">{b.total}</td>
                        <td className="px-4 py-3"><span className={`text-[11px] font-medium px-2.5 py-0.5 rounded-full ${statusColor(b.status)}`}>{statusIcon(b.status)}{b.status}</span></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {b.booking_id && <Link to={`/invoices/${b.booking_id}`} className="text-[11px] text-muted-foreground hover:text-primary transition-colors"><FileText size={13} className="inline" /></Link>}
                            {b.status === 'completed' && (reviewedIds.has(b.booking_id)
                              ? <span className="text-[11px] text-muted-foreground inline-flex items-center gap-0.5"><Star size={11} weight="fill" className="text-amber-400" /> Reviewed</span>
                              : <button onClick={() => setReviewFor(b)} className="text-[11px] font-medium text-primary hover:text-primary/80 transition-colors cursor-pointer" data-testid={`review-btn-${b.booking_id}`}>Leave a review</button>)}
                            {b.status === 'completed' && <Link to={b.rebookUrl} className="text-[11px] font-medium text-primary hover:text-primary/80 transition-colors">Book again</Link>}
                            {(b.status === 'pending' || b.status === 'confirmed') && (
                              <button onClick={() => setCancelFor(b._raw)} className="text-[11px] font-medium text-red-500 hover:text-red-600 transition-colors cursor-pointer" data-testid={`cancel-btn-${b.booking_id}`}>Cancel</button>
                            )}
                            {(b.status === 'confirmed' || b.status === 'completed') && (reportedIds.has(b.booking_id)
                              ? <span className="text-[11px] text-muted-foreground">Reported</span>
                              : <button onClick={() => setReportFor(b._raw)} className="text-[11px] font-medium text-muted-foreground hover:text-amber-600 transition-colors cursor-pointer" data-testid={`report-btn-${b.booking_id}`}>Report a problem</button>)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
            </div>

            {/* Quick actions */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link to="/ai-planner" className="bg-card rounded-2xl border border-border/70 shadow-soft card-lift p-4 flex items-center gap-3 fade-up" style={{ animationDelay: '0ms' }} data-testid="quick-ai-planner">
                <Lightning size={20} className="text-accent-coral shrink-0" />
                <div><p className="text-sm font-medium text-foreground">AI Planner</p><p className="text-xs text-muted-foreground">Plan your next event</p></div>
              </Link>
              <Link to="/browse" className="bg-card rounded-2xl border border-border/70 shadow-soft card-lift p-4 flex items-center gap-3 fade-up" style={{ animationDelay: '50ms' }} data-testid="quick-browse">
                <MagnifyingGlassIcon />
                <div><p className="text-sm font-medium text-foreground">Browse</p><p className="text-xs text-muted-foreground">Find providers</p></div>
              </Link>
              <Link to="/invoices" className="bg-card rounded-2xl border border-border/70 shadow-soft card-lift p-4 flex items-center gap-3 fade-up" style={{ animationDelay: '100ms' }} data-testid="quick-invoices">
                <FileText size={20} className="text-primary shrink-0" />
                <div><p className="text-sm font-medium text-foreground">Invoices</p><p className="text-xs text-muted-foreground">View & download</p></div>
              </Link>
              <Link to="/messages" className="bg-card rounded-2xl border border-border/70 shadow-soft card-lift p-4 flex items-center gap-3 fade-up" style={{ animationDelay: '150ms' }} data-testid="quick-messages">
                <ChatCircle size={20} className="text-primary shrink-0" />
                <div><p className="text-sm font-medium text-foreground">Messages</p><p className="text-xs text-muted-foreground">Chat with providers</p></div>
              </Link>
            </div>
          </div>
        )}

        {/* Bookings tab */}
        {tab === 'bookings' && displayBookings.length === 0 && (
          <div className="flex flex-col items-center text-center py-16 bg-card rounded-2xl border border-border/70 shadow-soft fade-up" data-testid="bookings-tab">
            <div className="bg-secondary rounded-full p-4 mb-4">
              <CalendarBlank size={28} className="text-primary" />
            </div>
            <p className="text-sm text-muted-foreground mb-5">No bookings yet — plan your first event and they'll show up here.</p>
            <Link to="/ai-planner" className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90">
              Plan an event <ArrowRight size={16} />
            </Link>
          </div>
        )}
        {tab === 'bookings' && displayBookings.length > 0 && (
          <div className="bg-card rounded-2xl border border-border/70 shadow-soft overflow-hidden overflow-x-auto fade-up" data-testid="bookings-tab">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 border-b border-border">
                <tr>
                  <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Provider</th>
                  <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Service</th>
                  <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Date</th>
                  <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Total</th>
                  <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Status</th>
                  <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {displayBookings.map((b, i) => (
                  <tr key={i} className="border-b border-border/60 last:border-0 hover:bg-secondary/50 transition-colors">
                    <td className="px-4 py-3 font-medium text-foreground">{b.provider}</td>
                    <td className="px-4 py-3 text-muted-foreground">{b.service}</td>
                    <td className="px-4 py-3 text-muted-foreground">{b.date}</td>
                    <td className="px-4 py-3 font-medium text-foreground">{b.total}</td>
                    <td className="px-4 py-3"><span className={`text-[11px] font-medium px-2.5 py-0.5 rounded-full ${statusColor(b.status)}`}>{statusIcon(b.status)}{b.status}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {b.balanceDue > 0 && (
                          <button onClick={() => payBalance(b.booking_id)}
                            disabled={payingId === b.booking_id}
                            className="text-[11px] font-semibold text-primary-foreground bg-primary rounded-lg px-3 py-1.5 btn-anim hover:bg-primary/90 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                            data-testid={`pay-balance-${b.booking_id}`}>
                            {payingId === b.booking_id ? 'Starting…' : `Pay balance $${Number(b.balanceDue).toLocaleString()}${b.balanceDueDate ? ` by ${b.balanceDueDate}` : ''}`}
                          </button>
                        )}
                        {b.status === 'completed' && (reviewedIds.has(b.booking_id)
                          ? <span className="text-[11px] text-muted-foreground inline-flex items-center gap-0.5"><Star size={11} weight="fill" className="text-amber-400" /> Reviewed</span>
                          : <button onClick={() => setReviewFor(b)} className="text-[11px] font-medium text-primary hover:text-primary/80 transition-colors cursor-pointer" data-testid={`review-btn-tab-${b.booking_id}`}>Leave a review</button>)}
                        {b.status === 'completed' && <Link to={b.rebookUrl} className="text-[11px] font-medium text-primary hover:text-primary/80 transition-colors">Book again</Link>}
                        {(b.status === 'pending' || b.status === 'confirmed') && (
                          <button onClick={() => setCancelFor(b._raw)} className="text-[11px] font-medium text-red-500 hover:text-red-600 transition-colors cursor-pointer" data-testid={`cancel-btn-tab-${b.booking_id}`}>Cancel</button>
                        )}
                        {(b.status === 'confirmed' || b.status === 'completed') && (reportedIds.has(b.booking_id)
                          ? <span className="text-[11px] text-muted-foreground">Reported</span>
                          : <button onClick={() => setReportFor(b._raw)} className="text-[11px] font-medium text-muted-foreground hover:text-amber-600 transition-colors cursor-pointer" data-testid={`report-btn-tab-${b.booking_id}`}>Report a problem</button>)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Quotes tab */}
        {tab === 'quotes' && (
          <div className="space-y-4" data-testid="quotes-tab">
            {quotes.length === 0 ? (
              <div className="flex flex-col items-center text-center py-16 bg-card rounded-2xl border border-border/70 shadow-soft fade-up">
                <div className="bg-secondary rounded-full p-4 mb-4"><ChatCircle size={28} className="text-primary" /></div>
                <p className="text-sm text-muted-foreground mb-5">No quote requests yet — ask any provider for a custom price from their profile.</p>
                <Link to="/browse" className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim shadow-soft hover:bg-primary/90">Browse providers <ArrowRight size={16} /></Link>
              </div>
            ) : quotes.map(q => (
              <div key={q.quote_id} className="bg-card rounded-2xl border border-border/70 shadow-soft p-5 fade-up" data-testid={`quote-${q.quote_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Link to={`/provider/${q.profile_id}`} className="font-display text-base font-semibold text-foreground hover:text-primary no-underline">{q.business_name}</Link>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide ${
                        q.status === 'quoted' ? 'bg-primary/10 text-primary' :
                        q.status === 'accepted' ? 'bg-emerald-500/10 text-emerald-600' :
                        q.status === 'declined' ? 'bg-red-500/10 text-red-500' : 'bg-amber-500/10 text-amber-600'}`}>
                        {q.status === 'requested' ? 'Waiting for reply' : q.status}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">{q.event_type}{q.event_date ? ` · ${q.event_date}` : ''}{q.guest_count ? ` · ${q.guest_count} guests` : ''}</p>
                    {q.status === 'quoted' && q.terms && <p className="text-xs text-muted-foreground mt-2 bg-secondary/50 rounded-lg px-3 py-2">{q.terms}</p>}
                    {q.status === 'quoted' && q.vendor_message && <p className="text-xs text-foreground/80 mt-2 italic">“{q.vendor_message}”</p>}
                  </div>
                  <div className="text-right shrink-0">
                    {q.status === 'quoted' && (
                      <>
                        <p className="text-2xl font-display font-semibold text-foreground tabular-nums">${Number(q.price).toLocaleString()}</p>
                        <p className="text-[10px] text-muted-foreground mb-2">+ 6% platform fee{q.deposit_pct ? ` · ${q.deposit_pct}% deposit to book` : ''}</p>
                        <button onClick={async () => {
                          setQuoteBusy(q.quote_id);
                          try {
                            const r = await axios.post(`${API}/quotes/${q.quote_id}/accept`, { origin_url: window.location.origin }, { headers });
                            if (r.data.url) { window.location.href = r.data.url; return; }
                          } catch (err) { console.error('Accept failed:', err); }
                          setQuoteBusy(null);
                        }} disabled={quoteBusy === q.quote_id}
                          className="bg-primary text-primary-foreground rounded-xl px-5 h-10 text-xs font-semibold btn-anim shadow-soft hover:bg-primary/90 disabled:opacity-50 cursor-pointer"
                          data-testid={`accept-quote-${q.quote_id}`}>
                          {quoteBusy === q.quote_id ? 'Opening…' : 'Accept & pay'}
                        </button>
                      </>
                    )}
                    {q.status === 'accepted' && <p className="text-xs text-emerald-600 font-medium inline-flex items-center gap-1"><Check size={12} weight="bold" /> Booked</p>}
                    {q.status === 'declined' && <p className="text-xs text-muted-foreground">Provider unavailable</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Calendar tab */}
        {tab === 'calendar' && (
          <BookingCalendar bookings={displayBookings} />
        )}

        {/* Favorites tab */}
        {tab === 'favorites' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="favorites-tab">
            {favorites.length === 0 ? (
              <div className="col-span-full flex flex-col items-center text-center py-16 bg-card rounded-2xl border border-border/70 shadow-soft fade-up">
                <div className="bg-secondary rounded-full p-4 mb-4">
                  <Heart size={28} className="text-primary" />
                </div>
                <p className="text-sm text-muted-foreground mb-5">You haven't saved any providers yet — tap the heart on any provider to save them here.</p>
                <Link to="/browse" className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90">
                  Browse services <ArrowRight size={16} />
                </Link>
              </div>
            ) : favorites.map((f, i) => (
              <Link key={f.profile_id} to={`/provider/${f.profile_id}`} className="bg-card rounded-2xl border border-border/70 shadow-soft card-lift p-5 fade-up" style={{ animationDelay: `${Math.min(i, 8) * 50}ms` }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-widest font-medium text-primary">{f.category}</span>
                  <div className="flex items-center gap-1"><Star size={12} weight="fill" className="text-amber-400" /><span className="text-xs text-muted-foreground">{f.rating_avg}</span></div>
                </div>
                <h3 className="font-display text-lg font-semibold text-foreground">{f.business_name}</h3>
                {(() => { const pr = providerPrice(f); return (
                <p className="text-xs text-muted-foreground mt-1">
                  {pr.isQuote ? 'Custom quote' : pr.isRange ? pr.display : <>From ${pr.amount}<span className="text-muted-foreground">{pr.suffixShort}</span></>}
                </p>
                ); })()}
              </Link>
            ))}
          </div>
        )}

      </div>

      {reviewFor && (
        <ReviewModal
          booking={reviewFor}
          headers={headers}
          onClose={() => setReviewFor(null)}
          onDone={() => { setReviewFor(null); fetchData(); }}
        />
      )}
      {cancelFor && (
        <CancelModal
          booking={cancelFor}
          headers={headers}
          onClose={() => setCancelFor(null)}
          onDone={() => { setCancelFor(null); toast.success('Booking cancelled. Any refund is on its way.'); fetchData(); }}
        />
      )}
      {reportFor && (
        <ReportModal
          booking={reportFor}
          headers={headers}
          onClose={() => setReportFor(null)}
          onDone={() => { setReportFor(null); toast.success('Report submitted — our team will review within 48 hours.'); fetchData(); }}
        />
      )}
    </div>
  );
}

function MagnifyingGlassIcon() {
  return <MagnifyingGlass size={20} className="text-primary shrink-0" />;
}

function BookingCalendar({ bookings }) {
  const [month, setMonth] = useState(new Date());
  const year = month.getFullYear();
  const m = month.getMonth();
  const firstDay = new Date(year, m, 1).getDay();
  const daysInMonth = new Date(year, m + 1, 0).getDate();
  const today = new Date(); today.setHours(0,0,0,0);
  const monthName = month.toLocaleString('default', { month: 'long', year: 'numeric' });

  // Map bookings to dates
  const bookingsByDate = {};
  bookings.forEach(b => {
    const d = b.booking_date || b.date;
    if (d) {
      // Normalize date format
      const dateStr = d.includes(',') ? '' : d; // Skip "Mar 15, 2026" format demo data
      if (dateStr) {
        if (!bookingsByDate[dateStr]) bookingsByDate[dateStr] = [];
        bookingsByDate[dateStr].push(b);
      }
    }
  });

  return (
    <div className="fade-up" data-testid="booking-calendar">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display text-lg font-semibold text-foreground">Your Bookings Calendar</h3>
        <div className="flex items-center gap-2">
          <button onClick={() => setMonth(new Date(year, m - 1, 1))} className="h-11 w-11 flex items-center justify-center hover:bg-secondary rounded-xl transition-colors cursor-pointer" aria-label="Previous month"><CaretLeft size={16} className="text-muted-foreground" /></button>
          <span className="text-sm font-medium text-foreground min-w-[140px] text-center">{monthName}</span>
          <button onClick={() => setMonth(new Date(year, m + 1, 1))} className="h-11 w-11 flex items-center justify-center hover:bg-secondary rounded-xl transition-colors cursor-pointer" aria-label="Next month"><CaretRight size={16} className="text-muted-foreground" /></button>
        </div>
      </div>
      <div className="bg-card border border-border/70 rounded-2xl shadow-soft overflow-hidden">
        {/* Day headers */}
        <div className="grid grid-cols-7 bg-secondary/50 border-b border-border">
          {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
            <div key={d} className="text-[11px] font-semibold text-muted-foreground text-center py-2.5">{d}</div>
          ))}
        </div>
        {/* Day cells */}
        <div className="grid grid-cols-7">
          {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} className="border-b border-r border-border/50 min-h-[80px]" />)}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dateStr = `${year}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isToday = new Date(year, m, day).getTime() === today.getTime();
            const dayBookings = bookingsByDate[dateStr] || [];
            const hasBooking = dayBookings.length > 0;

            return (
              <div key={day} className={`border-b border-r border-border/50 min-h-[80px] p-1.5 ${isToday ? 'bg-secondary/50' : ''}`}>
                <div className={`text-[11px] mb-1 ${isToday ? 'w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold' : 'text-muted-foreground'}`}>
                  {day}
                </div>
                {dayBookings.map((b, bi) => (
                  <div key={bi} className={`text-[9px] px-1.5 py-0.5 rounded-full mb-0.5 truncate font-medium ${
                    b.status === 'confirmed' || b.status === 'completed'
                      ? 'bg-emerald-500/10 text-emerald-700'
                      : b.status === 'pending'
                      ? 'bg-amber-500/10 text-amber-700'
                      : 'bg-secondary text-muted-foreground'
                  }`}>
                    {(b.status === 'confirmed' || b.status === 'completed') && <CheckCircle size={8} weight="fill" className="inline mr-0.5" />}
                    {b.provider || b.provider_name || 'Booking'}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
      {/* Legend */}
      <div className="flex gap-4 mt-3 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-emerald-500/20 rounded-full" /> Confirmed</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-amber-500/20 rounded-full" /> Pending</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-primary rounded-full" /> Today</span>
      </div>
    </div>
  );
}
